// ============================================================
// Visual Factory v4 — aligned with demo style (glass surfaces,
// Inter font, Omya blue), light background, with inline
// section descriptions so first-time viewers understand what
// each panel shows.
//
// Industrial principles kept from v3: single view (no rotation),
// ANDON dot per line (ISA-101 desaturated greens/oranges/reds),
// silo-truck collision logic as the main event, days-since-
// incident, no sensitive data (no plate / driver names).
// ============================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  silos, lineStatus, dailyPlan, maintenanceActive,
  siloMinutesToFull, siloCollision, planningData,
} from '@/data/mockData';
import { t, getLocale } from '@/i18n';
import { cn } from '@/lib/utils';
import { Maximize2, Minimize2, Info, ArrowLeft, Truck, Wrench, ShieldCheck, AlertTriangle, Zap, TrendingDown } from 'lucide-react';

// Brand-aligned colour tokens (kept inline to keep this page self-contained).
const PRIMARY = 'hsl(var(--chart-1))';
const ACCENT  = 'hsl(23, 81%, 52%)';
const ANDON = {
  green:  '#2D6A3E',   // ISA-101 desaturated, industrial
  yellow: '#C97A0E',
  red:    '#B0321F',
};

// ── helpers ────────────────────────────────────────────────────────
function fmtHM(min) {
  if (!isFinite(min)) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

function useTicker(intervalMs = 30000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick(x => x + 1), intervalMs);
    return () => clearInterval(i);
  }, [intervalMs]);
}

function useFullscreen() {
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  };
  return [isFs, toggle];
}

function currentTariff(now = new Date()) {
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  for (const tr of planningData.energyTariffs) {
    const [from, to] = tr.period.split('–');
    if (hhmm >= from && hhmm < to) return tr;
  }
  return planningData.energyTariffs[0];
}

// Solid LED dot — radial gradient + darker outline. No CSS glow.
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}
function lighten(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([r + (255 - r) * pct, g + (255 - g) * pct, b + (255 - b) * pct]);
}
function darken(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([r * (1 - pct), g * (1 - pct), b * (1 - pct)]);
}

const AndonLed = ({ status, size = 18 }) => {
  const color = ANDON[status] || '#5A5550';
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle at 30% 30%, ${lighten(color, 0.25)}, ${color} 70%)`,
        border: `1.5px solid ${darken(color, 0.35)}`,
        flexShrink: 0,
      }}
    />
  );
};

// ── Section header + inline description ───────────────────────────
const SectionHead = ({ title, hint, right }) => (
  <div className="flex items-end justify-between mb-3">
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{title}</div>
      <div className="text-xs text-slate-500/80 mt-0.5">{hint}</div>
    </div>
    {right}
  </div>
);

// Surface — glass card matching Dashboard / Supply Chain style.
const Surface = ({ className = '', style, children }) => (
  <div
    className={cn('bg-card border border-border shadow-sm rounded-xl', className)}
    style={style}
  >
    {children}
  </div>
);

// ── Plan banner — Omya-blue gradient hero (matches Dashboard hero) ─
function PlanBanner() {
  const pct = (dailyPlan.producedT / dailyPlan.targetT) * 100;
  const expectedAtThisPoint = (1 - dailyPlan.shiftRemainingHours / 24) * dailyPlan.targetT;
  const onPlan = dailyPlan.producedT >= expectedAtThisPoint * 0.97;

  return (
    <div
      className="rounded-xl px-8 py-6 text-white shadow-elevated relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--chart-1)))',
      }}
    >
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/[0.04]" />
      <div className="absolute right-1/3 bottom-0 w-32 h-32 rounded-full bg-white/[0.03]" />
      <div className="grid grid-cols-[auto_1fr_auto] gap-12 items-center relative">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold mb-1">{t('visualFactory.todayProduced')}</div>
          <div className="flex items-baseline gap-3">
            <span className="text-7xl font-bold tabular-nums leading-none">{dailyPlan.producedT.toLocaleString(getLocale()).replace(/\s/g, ' ')}</span>
            <span className="text-2xl text-white/60">/ {dailyPlan.targetT.toLocaleString(getLocale()).replace(/\s/g, ' ')} t</span>
          </div>
          <div className="text-xs text-white/60 mt-2">{t('visualFactory.bannerHint')}</div>
        </div>
        <div>
          <div className="relative h-7 bg-white/15 rounded-full overflow-hidden border border-white/10">
            <div
              className="absolute inset-y-0 left-0 transition-all rounded-full"
              style={{
                width: `${Math.min(100, pct)}%`,
                background: onPlan ? 'rgba(255,255,255,0.92)' : ANDON.red,
              }}
            />
            <div
              className="absolute top-0 bottom-0 w-px bg-white/80"
              style={{ left: `${(expectedAtThisPoint / dailyPlan.targetT) * 100}%` }}
              title={`${t('visualFactory.expected')}: ${Math.round(expectedAtThisPoint)} t`}
            />
          </div>
          <div className="flex justify-between text-[11px] text-white/60 mt-1.5 tabular-nums">
            <span>0</span>
            <span>{t('visualFactory.expected')}: {Math.round(expectedAtThisPoint).toLocaleString(getLocale()).replace(/\s/g, ' ')} t</span>
            <span>{dailyPlan.targetT.toLocaleString(getLocale()).replace(/\s/g, ' ')} t</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold mb-1">{t('visualFactory.shiftRemaining')}</div>
          <div className="text-5xl font-bold tabular-nums">{dailyPlan.shiftRemainingHours.toFixed(1)} h</div>
        </div>
      </div>
    </div>
  );
}

// ── Line tile ──────────────────────────────────────────────────────
function LineTile({ line }) {
  const tphPct = Math.round((line.currentTph / line.targetTph) * 100);
  return (
    <Surface className="p-5 flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <span className="text-lg font-semibold text-slate-900 tracking-wide">{t(line.name)}</span>
        <AndonLed status={line.statusAndon} size={18} />
      </div>
      <div className="text-sm text-slate-500 mb-3">{line.currentMaterial}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-bold tabular-nums text-slate-900">{line.currentTph}</span>
        <span className="text-base text-slate-400">/ {line.targetTph} t/h</span>
        <span className="ml-auto text-xs text-slate-400 tabular-nums">{tphPct} %</span>
      </div>
      {line.statusAndon !== 'green' && line.stopReason && (
        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: ANDON.yellow }}>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{t(line.stopReason)}</span>
          </span>
          {line.downtimeMin > 0 && (
            <span className="text-xs font-medium tabular-nums shrink-0" style={{ color: ANDON.red }}>⏱ {fmtHM(line.downtimeMin)}</span>
          )}
        </div>
      )}
    </Surface>
  );
}

// ── Silo row ───────────────────────────────────────────────────────
function SiloRow({ silo }) {
  const pct = Math.round((silo.contentT / silo.capacityT) * 100);
  const fillIn = siloMinutesToFull(silo);
  const coll = siloCollision(silo);
  const truck = silo.queuedTrucks[0];

  const isCrit = pct >= 95;
  const isWarn = pct >= 85;
  const barColor = isCrit ? ANDON.red : isWarn ? ANDON.yellow : ANDON.green;
  const [siloName, siloMat] = silo.name.split(' — ');

  return (
    <div
      className={cn(
        'grid items-center gap-4 px-4 py-3 rounded-xl backdrop-blur-sm border',
        coll.collision ? 'bg-rose-50/90 border-rose-300' : 'bg-white/70 border-border',
      )}
      style={{ gridTemplateColumns: '130px minmax(180px, 1fr) 130px 180px', boxShadow: coll.collision ? `0 0 0 1px ${ANDON.red} inset` : undefined }}
    >
      <div>
        <div className="text-base font-semibold text-slate-900">{siloName}</div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{siloMat}</div>
      </div>
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xl font-bold tabular-nums text-slate-900">{pct} %</span>
          <span className="text-xs text-slate-500 tabular-nums">{silo.contentT} / {silo.capacityT} t</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-200/70 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
        </div>
      </div>
      <div className="text-right" style={{ whiteSpace: 'nowrap' }}>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{t('visualFactory.fullIn')}</div>
        <div
          className="text-lg font-bold tabular-nums"
          style={{ color: pct >= 90 ? ANDON.red : 'rgb(15 23 42)' }}
        >
          {fmtHM(fillIn)}
        </div>
      </div>
      <div>
        {truck ? (
          <div
            className={cn(
              'px-3 py-2 rounded-lg border',
              coll.collision ? 'bg-rose-100/90 border-rose-400' : 'bg-white/80 border-slate-200',
            )}
            style={{ whiteSpace: 'nowrap' }}
          >
            <div className="flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-xs text-slate-500">{t('visualFactory.truckEta')}</span>
              <span className="text-sm font-semibold tabular-nums text-slate-900">{fmtHM(truck.etaMin)}</span>
            </div>
            {coll.collision && (
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] mt-0.5" style={{ color: ANDON.red }}>
                ▲ {t('visualFactory.collision')}
              </div>
            )}
          </div>
        ) : isWarn ? (
          <div className="px-3 py-2 rounded-lg border bg-amber-50 border-amber-300">
            <div className="text-xs font-medium flex items-center gap-1.5" style={{ color: ANDON.yellow }}>
              <AlertTriangle className="h-3.5 w-3.5" /> {t('visualFactory.noTruck')}
            </div>
          </div>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </div>
    </div>
  );
}

// ── Footer stats ───────────────────────────────────────────────────
function FooterStats() {
  const trucksWaiting = 2;
  const trucksOnSite = 7;
  return (
    <Surface className="px-8 py-4">
      <div className="grid grid-cols-4 gap-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{t('visualFactory.expeditionToday')}</div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-bold tabular-nums text-slate-900">{dailyPlan.trucksLoadedToday}</span>
            <span className="text-sm text-slate-400">/ {dailyPlan.trucksTargetToday}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1 tabular-nums">CZ {dailyPlan.trucksToCZ} · PL {dailyPlan.trucksToPL}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{t('visualFactory.trucksOnSite')}</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums text-slate-900">{trucksOnSite}</span>
            {trucksWaiting > 0 && (
              <span className="text-xs font-medium flex items-center gap-1" style={{ color: ANDON.yellow }}>
                <AlertTriangle className="h-3.5 w-3.5" /> {trucksWaiting} {t('visualFactory.waitingOver30')}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{t('visualFactory.maintenance')}</div>
          {maintenanceActive.map(m => (
            <div key={m.id} className="flex items-center gap-2">
              <Wrench className="h-4 w-4" style={{ color: ANDON.yellow }} />
              <span className="text-sm text-slate-700">{t(m.equipment)} · <span className="text-xs">ETA</span> <span className="font-semibold tabular-nums">{m.etaHour}</span></span>
            </div>
          ))}
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{t('visualFactory.daysSinceIncident')}</div>
          <div className="flex items-center justify-end gap-2">
            <ShieldCheck className="h-7 w-7" style={{ color: ANDON.green }} />
            <span className="text-3xl font-bold tabular-nums" style={{ color: ANDON.green }}>{dailyPlan.daysSinceIncident}</span>
          </div>
        </div>
      </div>
    </Surface>
  );
}

// ── Ticker — single-line tariff hint ───────────────────────────────
function Ticker() {
  const tariff = currentTariff();
  const palette = {
    peak:     { bg: 'bg-rose-50/90',    border: 'border-rose-200',    color: ANDON.red,    icon: Zap },
    negative: { bg: 'bg-sky-50/90',     border: 'border-sky-200',     color: PRIMARY,      icon: TrendingDown },
    offpeak:  { bg: 'bg-emerald-50/90', border: 'border-emerald-200', color: ANDON.green,  icon: Zap },
    standard: { bg: 'bg-amber-50/90',   border: 'border-amber-200',   color: ANDON.yellow, icon: Zap },
  }[tariff.type] || { bg: 'bg-white/70', border: 'border-slate-200', color: 'rgb(100 116 139)', icon: Zap };
  const Ic = palette.icon;
  const hint = tariff.type === 'peak' ? t('visualFactory.tickerPeak')
    : tariff.type === 'negative' ? t('visualFactory.tickerNegative')
    : tariff.type === 'offpeak'  ? t('visualFactory.tickerOffPeak')
    : t('visualFactory.tickerStandard');
  return (
    <div className={cn('px-6 py-2.5 rounded-xl border backdrop-blur-sm flex items-center gap-3', palette.bg, palette.border)}>
      <Ic className="h-4 w-4 shrink-0" style={{ color: palette.color }} />
      <span className="text-xs font-bold uppercase tracking-[0.12em]" style={{ color: palette.color }}>{tariff.type} {tariff.period}</span>
      <span className="text-slate-300">·</span>
      <span className="text-sm text-slate-700">{hint}</span>
    </div>
  );
}

// ── Info dialog — explains what's on screen ───────────────────────
function InfoButton() {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/80 backdrop-blur-sm border border-slate-200 text-xs text-slate-600 hover:bg-white shadow-sm"
        title={t('visualFactory.whatIsThis')}
      >
        <Info className="h-3.5 w-3.5" /> {t('visualFactory.whatIsThis')}
      </button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
      <div
        className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full mx-6 p-8 border border-border"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">{t('visualFactory.whatIsThis')}</h2>
          <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-900 text-sm">✕</button>
        </div>
        <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
          <p>{t('visualFactory.aboutIntro')}</p>
          <ul className="space-y-2.5">
            <li><strong>{t('visualFactory.todayProduced')}</strong> — {t('visualFactory.aboutBanner')}</li>
            <li><strong>{t('visualFactory.productionLines')}</strong> — {t('visualFactory.aboutLines')}</li>
            <li><strong>{t('visualFactory.silosAndTrucks')}</strong> — {t('visualFactory.aboutSilos')}</li>
            <li><strong>{t('visualFactory.footerInfo')}</strong> — {t('visualFactory.aboutFooter')}</li>
            <li><strong>{t('visualFactory.tariffTicker')}</strong> — {t('visualFactory.aboutTicker')}</li>
          </ul>
          <p className="text-xs text-slate-500 italic pt-3 border-t border-slate-200">{t('visualFactory.aboutFooterNote')}</p>
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────
export default function VisualFactory() {
  useTicker(30000);
  const [isFs, toggleFs] = useFullscreen();
  const now = new Date();
  const timeStr = now.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="fixed inset-0 overflow-y-auto"
      style={{
        background: 'linear-gradient(135deg, hsl(214,17%,97%), hsl(214,17%,94%))',
        fontFamily: "Inter, system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div className="min-h-screen flex flex-col gap-4 px-8 py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-card border border-border text-xs text-slate-600 hover:bg-white shadow-sm"
              title={t('visualFactory.backToApp')}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {t('visualFactory.backToApp')}
            </Link>
            <span className="text-xl font-bold tracking-tight" style={{ color: PRIMARY }}>OMYA</span>
            <span className="text-base text-slate-500">{t('visualFactory.title')}</span>
            <span className="px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-[10px] font-bold tracking-[0.12em] uppercase text-amber-800">
              {t('concept.badge')}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-white/70 border border-border text-[10px] font-bold tracking-[0.12em] uppercase text-slate-700">
              {t('dashboard.plant')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <InfoButton />
            <span className="text-xl font-bold tabular-nums text-slate-700">{timeStr}</span>
            <button
              onClick={toggleFs}
              className="p-2 rounded-lg bg-card border border-border hover:bg-white shadow-sm text-slate-600"
              title={t('visualFactory.exitFullscreen')}
            >
              {isFs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <PlanBanner />

        {/* Lines */}
        <div>
          <SectionHead
            title={t('visualFactory.productionLines')}
            hint={t('visualFactory.linesHint')}
          />
          <div className="grid grid-cols-3 gap-4">
            {lineStatus.map(l => <LineTile key={l.id} line={l} />)}
          </div>
        </div>

        {/* Silos & trucks */}
        <div className="flex-1 min-h-0 flex flex-col">
          <SectionHead
            title={t('visualFactory.silosAndTrucks')}
            hint={t('visualFactory.silosHint')}
            right={<span className="text-[11px] italic" style={{ color: ANDON.red }}>{t('visualFactory.collisionLegend')}</span>}
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 content-start">
            {silos.map(s => <SiloRow key={s.id} silo={s} />)}
          </div>
        </div>

        <FooterStats />
        <Ticker />
      </div>
    </div>
  );
}
